import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {
    semanticColor,
    sizing,
    spacing,
} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge, LabelSmall} from "@khanacademy/wonder-blocks-typography";
import type {ReadingStyle as ReadingStyleType} from "@khan/guide-types";

interface Props {
    style?: StyleType;
    currentReadingStyle: ReadingStyleType | null;
    onChange: (value: ReadingStyleType) => void;
    isLoading?: boolean;
}

/**
 * A component to allow users to select the "reading style", or complexity
 * level of the language with which the bot responds to them.
 */
const ReadingStyle = ({
    style,
    currentReadingStyle,
    onChange,
    isLoading,
}: Props): React.ReactElement => {
    const readingStyles: Record<ReadingStyleType, string> = {
        LOW: t`Simple `,
        MEDIUM: t`Standard`,
        HIGH: t`Professorial`,
    };
    const readingStyleDescriptions: Partial<Record<ReadingStyleType, string>> =
        {
            LOW: t`Simple and not a lot of words`,
            MEDIUM: t`Conversational with a more complex structure`,
            HIGH: t`Khanmigo will respond like a college professor`,
        };

    return (
        <View style={style}>
            <LabelLarge tag="label" htmlFor="select-reading-style">
                {t`Reading style`}
            </LabelLarge>
            <LabelSmall style={styles.subtitle}>
                {t`Choose the reading style you'd like Khanmigo to respond to you with.`}
            </LabelSmall>
            <Strut size={spacing.medium_16} />
            <SingleSelect
                id="select-reading-style"
                style={styles.select}
                placeholder={
                    isLoading && currentReadingStyle === null
                        ? t`Loading...`
                        : t`Select a reading style`
                }
                selectedValue={currentReadingStyle}
                onChange={(value) => onChange(value as ReadingStyleType)}
            >
                {Object.entries(readingStyles).map(([readingStyle, label]) => (
                    <OptionItem
                        key={readingStyle}
                        label={label}
                        value={readingStyle}
                    />
                ))}
            </SingleSelect>
            <Strut size={spacing.xxxxSmall_2} />
            <LabelSmall style={styles.subtitle}>
                {currentReadingStyle !== null
                    ? readingStyleDescriptions[currentReadingStyle]
                    : null}
            </LabelSmall>
        </View>
    );
};

const styles = StyleSheet.create({
    select: {
        width: "100%",
    },
    subtitle: {
        color: semanticColor.text.secondary,
        minBlockSize: sizing.size_080,
        display: "flex",
        alignItems: "center",
    },
});

export default React.memo(ReadingStyle);
